<template>
  <article class="job-item cmn-card job-card-split">
    <div class="job-inner">
      <div class="position">
        <!-- If showCompany is true AND (job.company_name or company_logo exists): Full Company Header -->
        <template v-if="showCompany && (job.company_name || job.company_logo_path || job.company_logo)">
          <!-- Top: Company Avatar, Name & Posted Time -->
          <div class="job-list-meta">
            <div class="job-avatar avatar-logo">
              <img v-if="job.company_logo_path || job.company_logo" :src="job.company_logo_path || job.company_logo"
                :alt="job.company_name" class="avatar-logo-img" />
              <img v-else src="@/assets/images/common/ico_logo.svg" :alt="job.company_name" class="avatar-logo-img" />
            </div>
            <div class="company-name-container">
              <router-link :to="resolvedCompanyDetailRoute" class="company-name-link">
                {{ job.company_name }}
                <span v-if="job.agency == 1" class="agency-tag">agency</span>
              </router-link>
              <time class="job-time" v-if="postedTimeText">{{ postedTimeText }}</time>
            </div>
          </div>

          <!-- Middle: Title & Short description -->
          <div class="job-info">
            <h3 class="job-title line-clamp line-clamp-1">
              {{ job.job_title }}
            </h3>
            <p class="job-short-desc line-clamp line-clamp-1" v-if="job.short_desc || job.description">
              {{ job.short_desc || job.description }}
            </p>
          </div>
        </template>

        <!-- If showCompany is false OR no company info (e.g. inside Company Detail page) -->
        <template v-else>
          <div class="job-list-meta">
            <h3 class="job-title line-clamp line-clamp-1">
              {{ job.job_title }}
            </h3>
            <time class="job-time" v-if="postedTimeText">{{ postedTimeText }}</time>
          </div>
          <p class="job-short-desc line-clamp line-clamp-1" v-if="job.short_desc || job.description">
            {{ job.short_desc || job.description }}
          </p>
        </template>

        <!-- Meta Row: Job Type Badge & Salary -->
        <div class="job-meta-row">
          <span class="job-type-badge" v-if="job.job_type">
            <app-icon name="clock-fill" size="16" />
            {{ jobTypeText }}
          </span>

          <div class="meta-item salary-item">
            <app-icon name="dollar" size="16" />
            <span>
              <!-- Both min and max -->
              <span v-if="job.min_salary && job.max_salary">
                {{ formatNumber(job.min_salary) }} - {{ formatNumber(job.max_salary) }} MMK
              </span>
              <!-- Min salary only -->
              <span v-else-if="job.min_salary">
                From {{ formatNumber(job.min_salary) }} MMK
              </span>
              <!-- Max salary only -->
              <span v-else-if="job.max_salary">
                Up to {{ formatNumber(job.max_salary) }} MMK
              </span>
              <!-- None -->
              <span v-else>Negotiable</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom: Location & More Detail Button (Dashed Split section) -->
    <div class="job-action">
      <div class="meta-item-group">
        <app-icon name="map-pin" size="18" />
        <div class="address-sec">
          <span class="address" v-if="regionName">{{ regionName }}</span>
          <span class="address" v-if="regionName && townName">၊</span>
          <span class="address" v-if="townName">{{ townName }}</span>
        </div>
      </div>

      <router-link :to="resolvedJobDetailRoute" class="btn-more-detail">
        <span>အသေးစိတ်ကြည့်ရန်</span>
        <app-icon name="right-arrow" width="10" height="20"/>
      </router-link>
    </div>
  </article>
</template>

<script>
import RegionJson from "@/assets/json/region.json";

export default {
  name: "JobCard",
  props: {
    job: {
      type: Object,
      required: true,
      default: () => ({})
    },
    showCompany: {
      type: Boolean,
      default: true
    },
    mode: {
      type: String,
      default: "applicant" // 'applicant' | 'company'
    },
    detailRoute: {
      type: [Object, String],
      default: null
    },
    companyRoute: {
      type: [Object, String],
      default: null
    }
  },
  computed: {
    uniqueId() {
      return this.job.job_id || this.job.id || Math.random().toString(36).substring(2, 9);
    },
    resolvedJobDetailRoute() {
      if (this.detailRoute) return this.detailRoute;
      const routeName = this.mode === "company" ? "jobPostJobDetail" : "jobListingJobDetail";
      return {
        name: routeName,
        params: { id: this.job.job_id || this.job.id }
      };
    },
    resolvedCompanyDetailRoute() {
      if (this.companyRoute) return this.companyRoute;
      const routeName = this.mode === "company" ? "jobPostCompanyDetail" : "jobListingCompanyDetail";
      return {
        name: routeName,
        params: { id: this.job.company_id }
      };
    },
    regionName() {
      if (this.job.region !== undefined && this.job.region !== null && this.job.region !== "") {
        return RegionJson[this.job.region] || this.job.region || "";
      }
      return this.job.region_name || "";
    },
    townName() {
      if (!this.job.town) return "";
      return this.job.town.endsWith("မြို့နယ်") ? this.job.town : this.job.town + "မြို့နယ်";
    },
    jobTypeText() {
      const typeMap = {
        "1": "Full-time",
        "2": "Part-time",
        "3": "Internship",
        "4": "Freelance",
        "5": "Remote Job"
      };
      return typeMap[String(this.job.job_type)] || (this.job.job_type ? String(this.job.job_type) : "သတ်မှတ်ထားခြင်းမရှိပါ");
    },
    postedTimeText() {
      const dateVal = this.job.created_at || this.job.posted_date;
      if (!dateVal) return "";
      return this.timeAgo(dateVal);
    }
  },
  methods: {
    formatNumber(value) {
      if (value === undefined || value === null || value === "") return "";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    timeAgo(dateString) {
      if (!dateString) return "";
      if (typeof dateString === "string" && (dateString.includes("ago") || dateString.includes("just now"))) {
        return dateString;
      }
      const parsedDate = new Date(String(dateString).replace(/-/g, "/"));
      if (isNaN(parsedDate.getTime())) return dateString;

      const seconds = Math.floor((new Date() - parsedDate) / 1000);
      if (seconds < 0) return dateString;

      let interval = Math.floor(seconds / 31536000);
      if (interval >= 1) return interval + (interval === 1 ? " year ago" : " years ago");

      interval = Math.floor(seconds / 2592000);
      if (interval >= 1) return interval + (interval === 1 ? " month ago" : " months ago");

      interval = Math.floor(seconds / 86400);
      if (interval >= 1) return interval + (interval === 1 ? " day ago" : " days ago");

      interval = Math.floor(seconds / 3600);
      if (interval >= 1) return interval + (interval === 1 ? " hour ago" : " hours ago");

      interval = Math.floor(seconds / 60);
      if (interval >= 1) return interval + (interval === 1 ? " minute ago" : " minutes ago");

      if (seconds >= 0 && seconds < 60) return "just now";

      return dateString;
    }
  }
};
</script>
